'use client';

import React, { useState } from 'react';
import { 
  Package, 
  Barcode, 
  Copy, 
  Check, 
  ShieldCheck, 
  ZoomIn, 
  AlertCircle 
} from 'lucide-react';
import VehicleBrandLogo from '@/components/VehicleBrandLogo';

interface ProductGalleryClientProps {
  images: string[];
  title: string;
  condition: string;
  sku: string;
  oem?: string;
  stockQuantity: number;
  make?: string;
}

export default function ProductGalleryClient({
  images,
  title,
  condition,
  sku,
  oem,
  stockQuantity,
  make,
}: ProductGalleryClientProps) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const validImages = images && images.length > 0 ? images : ['/images/parts4drive-hero-poster.webp'];
  const currentImg = validImages[activeIdx] || validImages[0];

  const handleCopy = (text: string, fieldName: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Stock status badge configuration
  let stockBadge = {
    bg: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    dot: 'bg-emerald-500',
    label: 'IN STOCK',
  };
  if (stockQuantity <= 0) {
    stockBadge = {
      bg: 'bg-rose-50 text-rose-800 border-rose-200',
      dot: 'bg-rose-500',
      label: 'OUT OF STOCK',
    };
  } else if (stockQuantity <= 5) {
    stockBadge = {
      bg: 'bg-amber-50 text-amber-800 border-amber-200',
      dot: 'bg-amber-500 animate-pulse',
      label: 'LOW STOCK ALERT',
    };
  }

  return (
    <div className="space-y-4">
      {/* Primary High-Resolution Image Container */}
      <div className="relative aspect-square w-full rounded-2xl overflow-hidden border border-slate-200 bg-white p-3 sm:p-4 shadow-sm group">
        <img
          src={currentImg}
          alt={`${condition} ${title}`}
          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
          loading="eager"
        />

        {/* Card-Aligned Floating Badges Matching Shop & Home Cards */}
        {/* Stock badge top-left */}
        <div className="absolute top-3 left-3 z-10">
          <span className="bg-[#0A1B4A]/90 backdrop-blur-xs text-white border-none text-[11px] sm:text-xs px-3 py-1 rounded-full font-bold shadow-xs">
            {stockQuantity} pcs
          </span>
        </div>

        {/* Condition badge top-right */}
        {condition && (
          <div className="absolute top-3 right-3 z-10">
            <span className="bg-white/95 text-slate-800 text-[11px] sm:text-xs font-bold px-3 py-1 rounded-full shadow-xs uppercase border border-slate-200/80">
              {condition}
            </span>
          </div>
        )}

        {/* Vehicle Make Logo Badge bottom-left */}
        {make && (
          <div className="absolute bottom-3 left-3 z-10 flex items-center gap-1.5 bg-[#0A1B4A]/90 backdrop-blur-xs border border-white/20 px-3 py-1 rounded-full shadow-xs">
            <VehicleBrandLogo brand={make} size="xs" variant="raw" className="size-3.5 object-contain" />
            <span className="text-[11px] sm:text-xs font-bold text-white tracking-tight">{make}</span>
          </div>
        )}

        {/* Image Counter bottom-right */}
        {validImages.length > 1 && (
          <div className="absolute bottom-3 right-3 z-10 bg-black/70 backdrop-blur-md text-white text-[10px] font-mono px-2.5 py-1 rounded-full">
            {activeIdx + 1} / {validImages.length} Photos
          </div>
        )}
      </div>

      {/* Thumbnail Carousel (if multi-photo) */}
      {validImages.length > 1 && (
        <div className="flex items-center gap-2.5 overflow-x-auto pb-1.5 pt-0.5">
          {validImages.map((img, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setActiveIdx(idx)}
              className={`size-16 sm:size-20 rounded-xl border-2 overflow-hidden shrink-0 bg-white p-1 transition-all cursor-pointer ${
                idx === activeIdx
                  ? 'border-[#0A1B4A] scale-105 shadow-md ring-2 ring-blue-500/20'
                  : 'border-slate-200 opacity-70 hover:opacity-100'
              }`}
            >
              <img
                src={img}
                alt={`${title} - Thumbnail ${idx + 1}`}
                className="w-full h-full object-contain"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}

      {/* SKU & Barcode Card with One-Click Clipboard Copy */}
      <div className="p-4 rounded-2xl bg-white border border-slate-200 space-y-3 shadow-xs">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
            <Barcode className="size-4 text-[#0A1B4A]" />
            Official Part SKU &amp; Barcode
          </span>
          <button
            type="button"
            onClick={() => handleCopy(sku, 'SKU')}
            className="text-xs font-bold text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 cursor-pointer transition-colors"
          >
            {copiedField === 'SKU' ? (
              <>
                <Check className="size-3 text-emerald-600" />
                <span className="text-emerald-600 font-bold">Copied!</span>
              </>
            ) : (
              <>
                <Copy className="size-3" />
                <span>Copy SKU</span>
              </>
            )}
          </button>
        </div>

        <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 font-mono font-bold text-xs sm:text-sm text-[#0A1B4A] text-center tracking-wider break-all select-all">
          {sku || 'UNASSIGNED-PART-SKU'}
        </div>

        {oem && (
          <div className="pt-2.5 border-t border-slate-100 flex items-center justify-between">
            <span className="text-xs text-slate-600 font-medium">OEM Cross-Reference:</span>
            <div className="flex items-center gap-2">
              <span className="font-mono font-bold text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                {oem}
              </span>
              <button
                type="button"
                onClick={() => handleCopy(oem, 'OEM')}
                className="text-slate-400 hover:text-slate-700 p-1 cursor-pointer"
                title="Copy OEM Ref"
              >
                {copiedField === 'OEM' ? <Check className="size-3 text-emerald-600" /> : <Copy className="size-3" />}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
